// Типы для протокола общения с воркером
type WorkerRequest =
    | { type: 'init'; id: string; canvas: OffscreenCanvas; animationData: any }
    | { type: 'destroy'; id: string };

type WorkerResponse = { type: 'ready'; id: string } | { type: 'error'; id: string; error: string };

type WorkerResponseCallback = (response: WorkerResponse) => void;

class LottieWorkerManager {
    private worker: Worker | null = null;
    private callbacks = new Map<string, WorkerResponseCallback>();
    private activeAnimations = new Set<string>();
    private workerFailed = false;

    private getWorker(): Worker | null {
        if (this.workerFailed) {
            return null;
        }

        if (!this.worker) {
            try {
                console.log('[LottieWorkerManager] Creating worker with type: module');
                this.worker = new Worker(new URL('./lottie.worker.ts', import.meta.url), { type: 'module' });
                console.log('[LottieWorkerManager] Worker created successfully');

                this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
                    const response = e.data;
                    const callback = this.callbacks.get(response.id);
                    if (callback) {
                        callback(response);
                        // Удаляем callback после получения ready/error
                        if (response.type === 'ready' || response.type === 'error') {
                            this.callbacks.delete(response.id);
                        }
                    }
                };
                this.worker.onerror = (error) => {
                    console.error('[LottieWorkerManager] Worker error:', error);
                    this.workerFailed = true;
                    // Отклоняем все ожидающие промисы
                    this.callbacks.forEach((callback) => {
                        callback({ type: 'error', id: '', error: 'Worker failed to initialize' });
                    });
                    this.callbacks.clear();
                };
            } catch (error) {
                console.error('[LottieWorkerManager] Failed to create worker:', error);
                this.workerFailed = true;
                return null;
            }
        }
        return this.worker;
    }

    public async initAnimation(id: string, canvas: OffscreenCanvas, animationData: any): Promise<void> {
        // Предотвращаем повторную инициализацию
        if (this.activeAnimations.has(id)) {
            return;
        }

        const worker = this.getWorker();
        if (!worker) {
            throw new Error('Worker is not available');
        }

        return new Promise((resolve, reject) => {
            this.callbacks.set(id, (response) => {
                if (response.type === 'ready') {
                    this.activeAnimations.add(id);
                    resolve();
                } else if (response.type === 'error') {
                    reject(new Error(response.error));
                }
            });

            try {
                const request: WorkerRequest = { type: 'init', id, canvas, animationData };
                worker.postMessage(request, [canvas]);
            } catch (error) {
                this.callbacks.delete(id);
                reject(error);
            }
        });
    }

    public destroyAnimation(id: string): void {
        const hasActiveAnimation = this.activeAnimations.has(id);
        const hasCallback = this.callbacks.has(id);

        if (!hasActiveAnimation && !hasCallback) {
            return;
        }

        this.activeAnimations.delete(id);
        this.callbacks.delete(id);

        if (this.worker && hasActiveAnimation) {
            const request: WorkerRequest = { type: 'destroy', id };
            this.worker.postMessage(request);
        }
    }
}

export const lottieWorkerManager = new LottieWorkerManager();
